// Form admin pra criar/editar SaaS Product.
// - Slug imutável após criação (input disabled em edit + helper text)
// - Secrets: input password; em edit, mostra "Mantido" quando hasKey + botão Substituir

import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createSaasProductSchema,
  type CreateSaasProductDto,
  type CreateSaasProductInput,
} from '@control-center/shared';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { cn } from '@control-center/ui';

import { toast } from 'sonner';

import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { firstErrorMessage } from '../../lib/form-errors';
import type { SaasProductEntity } from '../../lib/saas-products-api';

export interface SaasProductFormProps {
  defaultValues?: Partial<CreateSaasProductInput>;
  /** Em modo edit, `slug` fica imutável e secrets viram opcionais "Substituir". */
  editMode?: boolean;
  hasBootstrapKey?: boolean;
  hasWebhookSecret?: boolean;
  isPending?: boolean;
  onSubmit: (values: CreateSaasProductDto) => void | Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
}

const TYPE_OPTIONS: { value: SaasProductEntity['type']; label: string }[] = [
  { value: 'saas_mensal', label: 'SaaS mensal' },
  { value: 'servico_unico', label: 'Serviço único' },
  { value: 'consultoria_recorrente', label: 'Consultoria recorrente' },
  { value: 'implantacao', label: 'Implantação' },
  { value: 'produto_digital', label: 'Produto digital' },
  { value: 'desenvolvimento_personalizado', label: 'Desenvolvimento personalizado' },
];

const STATUS_OPTIONS: { value: SaasProductEntity['status']; label: string }[] = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'em_desenvolvimento', label: 'Em desenvolvimento' },
  { value: 'pausado', label: 'Pausado' },
  { value: 'descontinuado', label: 'Descontinuado' },
];

const ENV_OPTIONS: { value: SaasProductEntity['environment']; label: string }[] = [
  { value: 'producao', label: 'Produção' },
  { value: 'homologacao', label: 'Homologação' },
  { value: 'interno', label: 'Interno' },
  { value: 'cliente_dedicado', label: 'Cliente dedicado' },
];

const LEVEL_OPTIONS: { value: SaasProductEntity['integrationLevel']; label: string }[] = [
  { value: 'manual', label: 'Manual (nível 0)' },
  { value: 'read_only', label: 'Read-only (nível 1)' },
  { value: 'full', label: 'Full (nível 2)' },
];

const IDENTITY_OPTIONS: { value: SaasProductEntity['identityMode']; label: string }[] = [
  { value: 'email', label: 'Por e-mail' },
  { value: 'subject', label: 'Pelo login do sistema (passe assinado)' },
];

const EMPTY: CreateSaasProductInput = {
  slug: '',
  name: '',
  description_short: '',
  description_long: '',
  type: 'saas_mensal',
  status: 'ativo',
  url: '',
  admin_url: '',
  email_logo_url: '',
  environment: 'producao',
  technical_user_id: null,
  commercial_user_id: null,
  observations: '',
  integration_level: 'manual',
  identity_mode: 'email',
  asaas_account_id: null,
  api_url: '',
  bootstrap_api_key: '',
  webhook_secret: '',
  rate_limit_per_min: 100,
};

export function SaasProductForm({
  defaultValues,
  editMode,
  hasBootstrapKey,
  hasWebhookSecret,
  isPending,
  onSubmit,
  onCancel,
  submitLabel,
}: SaasProductFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CreateSaasProductInput>({
    resolver: zodResolver(createSaasProductSchema),
    defaultValues: { ...EMPTY, ...defaultValues },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const [showBootstrap, setShowBootstrap] = useState(false);
  const [showWebhook, setShowWebhook] = useState(false);

  useEffect(() => {
    if (defaultValues) reset({ ...EMPTY, ...defaultValues });
  }, [defaultValues, reset]);

  function submit(values: CreateSaasProductInput): void | Promise<void> {
    const parsed = createSaasProductSchema.parse(values);
    return onSubmit(parsed);
  }

  return (
    <form
      onSubmit={(e) =>
        void handleSubmit(submit, (errs) =>
          toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
        )(e)
      }
      className="space-y-5"
      noValidate
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Nome" required error={errors.name?.message}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="text"
              placeholder="Reservei"
              hasError={Boolean(errors.name)}
              aria-describedby={describedBy}
              {...register('name')}
            />
          )}
        </Field>

        <Field
          label="Slug"
          required
          hint={editMode ? 'Imutável após criação.' : 'snake_case ou kebab-case. Único.'}
          error={errors.slug?.message}
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="text"
              placeholder="reservei"
              disabled={editMode}
              hasError={Boolean(errors.slug)}
              aria-describedby={describedBy}
              {...register('slug')}
            />
          )}
        </Field>
      </div>

      <Field label="Descrição curta" error={errors.description_short?.message}>
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            type="text"
            placeholder="Ex: Sistema de agendamento pra clínicas estéticas"
            maxLength={280}
            hasError={Boolean(errors.description_short)}
            aria-describedby={describedBy}
            {...register('description_short')}
          />
        )}
      </Field>

      <Field label="Descrição completa" error={errors.description_long?.message}>
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            rows={3}
            maxLength={5000}
            hasError={Boolean(errors.description_long)}
            aria-describedby={describedBy}
            {...register('description_long')}
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <Field label="Tipo" error={errors.type?.message}>
          {({ inputId }) => (
            <Select id={inputId} hasError={Boolean(errors.type)} {...register('type')}>
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Status" error={errors.status?.message}>
          {({ inputId }) => (
            <Select id={inputId} hasError={Boolean(errors.status)} {...register('status')}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Ambiente" error={errors.environment?.message}>
          {({ inputId }) => (
            <Select
              id={inputId}
              hasError={Boolean(errors.environment)}
              {...register('environment')}
            >
              {ENV_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="URL pública" error={errors.url?.message}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="url"
              placeholder="https://reservei.app"
              hasError={Boolean(errors.url)}
              aria-describedby={describedBy}
              {...register('url')}
            />
          )}
        </Field>

        <Field label="URL admin" error={errors.admin_url?.message}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="url"
              placeholder="https://admin.reservei.app"
              hasError={Boolean(errors.admin_url)}
              aria-describedby={describedBy}
              {...register('admin_url')}
            />
          )}
        </Field>

        {/* Marca do produto nos e-mails: o cabecalho usa esta logo e o remetente
            usa o `name` acima. Fica junto das outras URLs porque e o mesmo tipo
            de dado — nao inventei secao nova. */}
        <Field label="Logo dos e-mails" error={errors.email_logo_url?.message}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="url"
              placeholder="https://reservei.app.br/email-logo.png"
              hasError={Boolean(errors.email_logo_url)}
              aria-describedby={describedBy}
              {...register('email_logo_url')}
            />
          )}
        </Field>

        {/* Destino do botao "continuar assinando" nos e-mails. Mandar pra
            pagina do proprio SaaS e melhor que pro checkout do CC: o cliente
            reconhece o dominio, chega autenticado (o app trata o login e
            devolve pra mesma tela) e, no Android, abre direto no app
            instalado. Vazio = cai no checkout do Control Center. */}
        <Field label="URL de assinatura" error={errors.subscription_url?.message}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="url"
              placeholder="https://reservei.app.br/app/assinatura"
              hasError={Boolean(errors.subscription_url)}
              aria-describedby={describedBy}
              {...register('subscription_url')}
            />
          )}
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field label="Nível de integração" error={errors.integration_level?.message}>
          {({ inputId }) => (
            <Select
              id={inputId}
              hasError={Boolean(errors.integration_level)}
              {...register('integration_level')}
            >
              {LEVEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Rate limit (req/min)" error={errors.rate_limit_per_min?.message}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="number"
              min={1}
              max={10000}
              hasError={Boolean(errors.rate_limit_per_min)}
              aria-describedby={describedBy}
              {...register('rate_limit_per_min')}
            />
          )}
        </Field>
      </div>

      {/* Peça 3 do funil único: decide se a compra exige o passe do SaaS e se o
          acesso vai pro usuário+empresa dele (caixa de saída) ou pelo e-mail. */}
      <Field
        label="Como o cliente entra na compra"
        hint="Login do sistema: a compra só aceita quem vem logado dele, e o acesso vai pro usuário certo. E-mail: qualquer pessoa compra pelo e-mail."
        error={errors.identity_mode?.message}
      >
        {({ inputId, describedBy }) => (
          <Select
            id={inputId}
            hasError={Boolean(errors.identity_mode)}
            aria-describedby={describedBy}
            {...register('identity_mode')}
          >
            {IDENTITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Field
          label="API URL"
          hint="Endpoint base do SaaS pra bootstrap + webhooks"
          error={errors.api_url?.message}
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              type="url"
              placeholder="https://api.reservei.app/v1"
              hasError={Boolean(errors.api_url)}
              aria-describedby={describedBy}
              {...register('api_url')}
            />
          )}
        </Field>

        <Field
          label="Asaas Account ID"
          hint="UUID da conta Asaas que cobra esse produto. Opcional."
          error={errors.asaas_account_id?.message}
        >
          {({ inputId, describedBy }) => (
            <Controller
              control={control}
              name="asaas_account_id"
              render={({ field }) => (
                <Input
                  id={inputId}
                  type="text"
                  placeholder="opcional"
                  hasError={Boolean(errors.asaas_account_id)}
                  aria-describedby={describedBy}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  onBlur={field.onBlur}
                />
              )}
            />
          )}
        </Field>
      </div>

      <fieldset className="space-y-3 rounded-[10px] border border-line p-4">
        <legend className="px-2 text-[12px] font-medium text-ink-2">
          Credenciais técnicas (criptografadas at-rest)
        </legend>

        <Field
          label="Bootstrap API Key"
          hint={
            editMode && hasBootstrapKey
              ? 'Já definido. Deixe vazio pra manter; preencha pra substituir.'
              : 'Token usado pra puxar dados iniciais do SaaS conectado.'
          }
          error={errors.bootstrap_api_key?.message}
        >
          {({ inputId, describedBy }) => (
            <div className="relative">
              <Input
                id={inputId}
                type={showBootstrap ? 'text' : 'password'}
                placeholder={editMode && hasBootstrapKey ? '•••••••• (definido)' : 'sk_xxx...'}
                hasError={Boolean(errors.bootstrap_api_key)}
                aria-describedby={describedBy}
                className="pr-10"
                {...register('bootstrap_api_key')}
              />
              <button
                type="button"
                onClick={() => setShowBootstrap((v) => !v)}
                aria-label={showBootstrap ? 'Esconder' : 'Mostrar'}
                aria-pressed={showBootstrap}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-ink"
              >
                {showBootstrap ? (
                  <EyeOff size={14} strokeWidth={2} />
                ) : (
                  <Eye size={14} strokeWidth={2} />
                )}
              </button>
            </div>
          )}
        </Field>

        <Field
          label="Webhook Secret"
          hint={
            editMode && hasWebhookSecret
              ? 'Já definido. Deixe vazio pra manter; preencha pra substituir.'
              : 'Usado pra validar HMAC dos webhooks vindos do SaaS.'
          }
          error={errors.webhook_secret?.message}
        >
          {({ inputId, describedBy }) => (
            <div className="relative">
              <Input
                id={inputId}
                type={showWebhook ? 'text' : 'password'}
                placeholder={editMode && hasWebhookSecret ? '•••••••• (definido)' : 'whsec_xxx...'}
                hasError={Boolean(errors.webhook_secret)}
                aria-describedby={describedBy}
                className="pr-10"
                {...register('webhook_secret')}
              />
              <button
                type="button"
                onClick={() => setShowWebhook((v) => !v)}
                aria-label={showWebhook ? 'Esconder' : 'Mostrar'}
                aria-pressed={showWebhook}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-7 w-7 items-center justify-center rounded-[6px] text-muted hover:bg-panel-3 hover:text-ink"
              >
                {showWebhook ? (
                  <EyeOff size={14} strokeWidth={2} />
                ) : (
                  <Eye size={14} strokeWidth={2} />
                )}
              </button>
            </div>
          )}
        </Field>
      </fieldset>

      <Field label="Observações" error={errors.observations?.message}>
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            rows={2}
            maxLength={5000}
            hasError={Boolean(errors.observations)}
            aria-describedby={describedBy}
            {...register('observations')}
          />
        )}
      </Field>

      <div className={cn('flex items-center justify-end gap-2 pt-2')}>
        {onCancel && (
          <PillButton type="button" onClick={onCancel} disabled={isPending}>
            Cancelar
          </PillButton>
        )}
        <PillButton type="submit" solid disabled={isPending}>
          {isPending ? 'Salvando…' : (submitLabel ?? 'Salvar produto')}
        </PillButton>
      </div>
    </form>
  );
}
